<script lang="ts">
    import { getContext, onDestroy } from 'svelte'
    import Dropzone from 'svelte-file-dropzone'
    import { derived as sderived } from 'svelte/store'
    import type { Readable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import { filter, orderBy } from 'lodash-es'
    import { DateTime, Duration } from 'luxon'

    import { gettext as _ } from 'hydria/services/gettext'
    import { lg } from 'hydria/services/language'
    import { notify } from 'hydria/services/notifications'
    import { session, simpleFetch } from 'hydria/services/session'
    import { asAny, sanchor, spath } from 'hydria/svelte_helpers'
    import { makeSortString } from 'hydria/utils/strings'
    import { luxonLocale, toLuxon } from 'hydria/utils/time'
    import { type } from 'hydria/utils/type'

    import type { User, UserHandler } from './user'

    import CodeMirror from 'admin/components/CodeMirror.svelte'
    import Date from 'hydria/components/Date.svelte'
    import HumanizedBytes from 'hydria/components/HumanizedBytes.svelte'
    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'
    import InternationalPhone from 'wdom/components/contacts/InternationalPhone.svelte'
    import TokenAnchorProgress from './TokenAnchorProgress.svelte'

    const userh = getContext('User') as UserHandler
    let user: Readable<User> = sderived(userh.userWritable, ($v) => $v)

    const status = sderived(userh.status, ($status) => $status)
    const loading = sderived(userh.loading, ($loading) => $loading)

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    type SortMode = 'ctime' | 'domain' | 'filename' | 'type'
    let sortMode: SortMode = $state('type')
    let textFilter: string = $state('')
    let showEmail: boolean = $state(false)
    let showRevisions: boolean = $state(false)
    let showHidden: boolean = $state(false)
    let collapsed: boolean = $state(true)
    let istatus: null | 'loading' | 'ready' | 'error' = $state(null)

    const LAST_VERIF_DELTA = Duration.fromObject({ days: 30 })
    interface IntFileType {
        type: string
        description: string
        mimeTypes: string[] | RegExp[]
        sortOrder?: number
    }
    const INT_FILE_TYPES: IntFileType[] = [
        {
            type: 'pdf',
            description: 'PDF',
            mimeTypes: ['application/pdf'],
        },
        {
            type: 'ms',
            description: 'MS Office',
            mimeTypes: [/^application\/vnd\.openxmlformats-officedocument\./],
            // mimeTypes: [
            //     'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            //     'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            //     'application/vnd.openxmlformats-officedocument.presentationml.presentation'
            // ]
        },
        {
            type: 'image',
            description: 'Images',
            mimeTypes: [/^image\//],
        },
        {
            // Must be second last (before nofile)
            type: 'other',
            description: 'Autre',
            mimeTypes: [/./],
            sortOrder: 1000,
        },
        {
            // Specially handled
            type: 'email',
            description: 'Email',
            mimeTypes: [],
            sortOrder: 1002,
        },
        {
            // Specially handled
            type: 'nofile',
            description: 'Sans fichier',
            mimeTypes: [],
            sortOrder: 1001,
        },
    ]
    const INT_FILE_TYPES_BY_TYPE = {}
    let _sortOrder = 100
    for (const ift of INT_FILE_TYPES) {
        INT_FILE_TYPES_BY_TYPE[ift.type] = ift
        if (ift.sortOrder == null) ift.sortOrder = _sortOrder++
    }

    interface Interaction {
        id: string
        author_id: string
        ctime: DateTime
        tags: string[]
        content_origin: null | 'custom-email'
        content_text?: string
        content_html?: string
        // sending an email manually fills this up..
        content_extra?: { subject: string; sent: boolean }
        author_ip: string
        file?: {
            data_hash: {
                md5: string
                sha1: string
                sha256: string
            }
            mime_description: string
            mime_extension: string
            mime_type: string
            original_ext: string
            original_name: string
            size: number
            storage_name: string
            last_verification?: DateTime
            last_data_hash?: { md5: string }
            content_ok?: boolean

            // Artificially added by us..
            fullFilename: string
            filenameSort: string
            shouldReverify: boolean
        }
        name?: string
        org?: string
        email?: string
        phone?: string
        domain?: string
        hidden?: boolean

        // Artificially added in the backend
        author_name: string

        // Artificially added by us..
        domainSort: string
        // type: 'nofile' | 'email'
        type: string
        isLatestRevision: boolean
        _contentSelection: 'html' | 'text' | null
    }

    interface Group {
        groupString: string
        title: string
        entries: Interaction[]
    }

    let rawEntries: Interaction[] = $state([])
    let sortedEntries: Interaction[] = $state([])

    let fileIdsBeingValidated: Set<string> = $state(new Set())
    let entryIdsBeingMasked: Set<string> = $state(new Set())

    let lastVerifThreshold = DateTime.now().minus(LAST_VERIF_DELTA)

    async function load() {
        istatus = 'loading'

        try {
            const r = await simpleFetch('GET', `/json/wdom-user/1/${$user.id}/interactions`)
            importEntries(r.data)
            istatus = 'ready'
        } catch (e) {
            console.error('Error loading interactions:', e)
            notify({
                type: 'danger',
                title: 'Journal et fichiers',
                text: 'Le journal n’a pas pu être chargé.',
            })
            istatus = 'error'
        }
    }

    unsubs.push(
        status.subscribe(($status) => {
            if ($status == 'ready') {
                load()
            } else {
                rawEntries = []
            }
        }),
    )

    unsubs.push(
        luxonLocale.subscribe(($luxonLocale) => {
            // pour updater le nom du groupe qui est une date localisée.
            if (sortMode == 'ctime' && entryGroups && entryGroups.length) groupEntries(filteredEntries)
        }),
    )

    let rawDataEntryCache: Interaction[]
    function importEntries(data: any = null) {
        let items: Interaction[]
        if (data != null) {
            const filenameSortText = {}
            for (const entry of data.entries) {
                if (entry.ctime) entry.ctime = toLuxon(entry.ctime)
            }
            rawDataEntryCache = orderBy(data.entries, [(o) => o.ctime], ['desc'])
        }
        items = [...rawDataEntryCache]

        let nameIndex: { [key: string]: Interaction } = {}

        for (const entry of items) {
            entry.domainSort = entry.domain ? makeSortString(entry.domain) : 'zzzzzzzzzzzzzzzz'
            if (entry.file == null) {
                if (entry.content_origin == 'custom-email') {
                    entry.type = 'email'
                } else {
                    entry.type = 'nofile'
                }
                entry.isLatestRevision = true
                continue
            }

            entry.file.fullFilename = `${entry.file.original_name}${entry.file.original_ext}`
            entry.file.filenameSort = makeSortString(entry.file.fullFilename)

            if (entry.file.last_verification) {
                entry.file.last_verification = toLuxon(entry.file.last_verification)
                entry.file.shouldReverify = entry.file.last_verification < lastVerifThreshold
            }

            // entry.file.urlDownload = `/json/wdom-user/1/${$user.id}/interaction-file/${entry.id}/attachment?hydria-token=${session.token}`
            // entry.file.urlView = `/json/wdom-user/1/${$user.id}/interaction-file/${entry.id}/inline?hydria-token=${session.token}`

            if (!entry.hidden && nameIndex[entry.file.fullFilename] != null && nameIndex[entry.file.fullFilename].hidden) {
                nameIndex[entry.file.fullFilename].isLatestRevision = false
                delete nameIndex[entry.file.fullFilename]
            }
            entry.isLatestRevision = nameIndex[entry.file.fullFilename] == null
            if (entry.isLatestRevision) {
                nameIndex[entry.file.fullFilename] = entry
            }

            for (const fType of Object.values(INT_FILE_TYPES)) {
                for (const mimeType of fType.mimeTypes) {
                    if (type(mimeType) == 'regexp') {
                        if (entry.file.mime_type.match(mimeType)) {
                            entry.type = fType.type
                            break
                        }
                    } else if (entry.file.mime_type == mimeType) {
                        entry.type = fType.type
                        break
                    }
                }
                if (entry.type != null) break
            }
        }

        // no .clear() to trigger reactivity
        fileIdsBeingValidated = new Set()
        entryIdsBeingMasked = new Set()

        rawEntries = items as Interaction[]
        sortEntries(sortMode)
    }

    function sortEntries(mode: SortMode) {
        let iteratees: any = [(o: Interaction) => o.ctime]
        let orders: Array<boolean | 'asc' | 'desc'> = ['desc']

        if (mode == 'filename' || mode == 'type') {
            iteratees.unshift((o: Interaction) => {
                if (o.file != null) return o.file.filenameSort
                else 'zzzzzzzzzzzzzzzz'
            })
            orders.unshift('asc')
        }

        if (mode == 'type') {
            iteratees.unshift((o: Interaction) => INT_FILE_TYPES_BY_TYPE[o.type].sortOrder)
            orders.unshift('asc')
        }

        if (mode == 'domain') {
            iteratees.unshift((o: Interaction) => o.domainSort)
            orders.unshift('asc')
        }

        sortedEntries = orderBy(rawEntries, iteratees, orders)
    }

    function filterEntries(sortedEntries: Interaction[], textFilter: string, showEmail: boolean, showRevisions: boolean, showHidden: boolean) {
        const text = textFilter.trim()
        const textEreg = text ? new RegExp(text.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&'), 'ig') : null

        let filteredEntries: Interaction[] = filter(sortedEntries, (o: Interaction) => {
            const base = (showRevisions || o.isLatestRevision) && (showHidden || !o.hidden) && (!showEmail || o.content_origin == 'custom-email')
            if (!textEreg || !base) return base

            for (const key of ['content_text', 'name', 'org', 'email', 'phone', 'domain']) {
                if (o[key] != null && o[key].match(textEreg)) return true
            }
            if (o.file != null && o.file.fullFilename && o.file.fullFilename.match(textEreg)) return true
            if (o.tags && o.tags.length && o.tags.includes(text)) return true

            return false
        })
        return filteredEntries
    }

    function groupEntries(filteredEntries: Interaction[]) {
        const groupIndex: { [key: string]: Group } = {}
        let makeGroupAndAddEntry: (entry: Interaction) => Group

        let localEntryGroups: Group[] = []

        // Simple helper
        function makeGroupMaker(gstringFct: (entry: Interaction) => string, titleFct: (entry: Interaction) => string): (entry: Interaction) => Group {
            return (entry: Interaction) => {
                const gstring: string = gstringFct(entry)
                if (groupIndex[gstring] == null) {
                    groupIndex[gstring] = {
                        groupString: gstring,
                        title: titleFct(entry),
                        entries: [],
                    }
                    localEntryGroups.push(groupIndex[gstring])
                }
                groupIndex[gstring].entries.push(entry)
                return groupIndex[gstring]
            }
        }

        switch (sortMode) {
            default:
                notify({
                    type: 'danger',
                    title: 'Groupement Journal et Fichiers',
                    text: `Le mode de tri “${sortMode}” est non supporté ?`,
                })
            case 'ctime':
                makeGroupAndAddEntry = makeGroupMaker(
                    (entry) => entry.ctime.toFormat('yyyy-LL-dd'),
                    (entry) => entry.ctime.toLocaleString(DateTime.DATE_FULL, { locale: $luxonLocale }),
                )
                break
            case 'domain':
                makeGroupAndAddEntry = makeGroupMaker(
                    (entry) => (entry.domain ? entry.domain : ''),
                    (entry) => (entry.domain ? entry.domain : '(sans domaine)'),
                )
                break
            case 'filename':
                makeGroupAndAddEntry = makeGroupMaker(
                    (entry) => (entry.file ? entry.file.fullFilename : ''),
                    (entry) => (entry.file ? entry.file.fullFilename : '(sans fichier)'),
                )
                break
            case 'type':
                makeGroupAndAddEntry = makeGroupMaker(
                    (entry) => entry.type,
                    (entry) => INT_FILE_TYPES_BY_TYPE[entry.type].description,
                )
                break
        }

        // localEntryGroups.length = 0
        filteredEntries.map((entry) => makeGroupAndAddEntry(entry))
        // Trigger reactivity
        return localEntryGroups
    }

    // ****************************************************************************** Listing actions
    async function validateFileContent(entry: Interaction) {
        fileIdsBeingValidated.add(entry.id)
        fileIdsBeingValidated = fileIdsBeingValidated
        try {
            const r = await simpleFetch('POST', `/json/wdom-user/1/${$user.id}/interaction/${entry.id}/verify-content`)
            r.data.entry.ctime = toLuxon(r.data.entry.ctime)
            for (const [i, e] of rawDataEntryCache.entries()) {
                if (e.id === entry.id) {
                    rawDataEntryCache.splice(i, 1, r.data.entry)
                    importEntries()
                    break
                }
            }
        } catch (e) {
            console.error('Failed to verify file contents:', e)
            notify({
                type: 'danger',
                title: 'Vérification de fichier',
                text: 'Le contenu du fichier n’a pas pu être validé.',
            })
            fileIdsBeingValidated.delete(entry.id)
            fileIdsBeingValidated = fileIdsBeingValidated
        }
    }

    async function toggleMask(entry: Interaction) {
        entryIdsBeingMasked.add(entry.id)
        entryIdsBeingMasked = entryIdsBeingMasked
        try {
            const r = await simpleFetch(entry.hidden ? 'DELETE' : 'PUT', `/json/wdom-user/1/${$user.id}/interaction/${entry.id}/hidden`)
            r.data.entry.ctime = toLuxon(r.data.entry.ctime)
            for (const [i, e] of rawDataEntryCache.entries()) {
                if (e.id === entry.id) {
                    rawDataEntryCache.splice(i, 1, r.data.entry)
                    importEntries()
                    break
                }
            }
        } catch (e) {
            console.error('Failed to toggle mask:', e)
            notify({
                type: 'danger',
                title: 'Masquer',
                text: 'L’entrée de journal n’a pas pu être masquée suite à une erreur.',
            })
            entryIdsBeingMasked.delete(entry.id)
            entryIdsBeingMasked = entryIdsBeingMasked
        }
    }

    // ****************************************************************************** New interaction

    let saving = $state(false)
    let saveError: string
    interface InteractionForm {
        content_text: string
        name: string
        org: string
        email: string
        phone: string
        domain: string
        tags: string[]
    }
    let form: InteractionForm = $state()
    let tagEdit = $state('')
    let tagElement: HTMLElement = $state()
    let selectedFile: File | null = $state(null)
    let selectedFileName: string | null = $state()

    function emptyForm() {
        form = {
            content_text: '',
            name: '',
            org: '',
            email: '',
            phone: '',
            domain: '',
            tags: [],
        }
        tagEdit = ''
        selectedFile = null
        selectedFileName = null
    }
    emptyForm()

    function addTag() {
        const tag = tagEdit.trim()
        if (tag) {
            let tagSet = new Set(form.tags)
            tagSet.add(tag)
            form.tags = [...tagSet].sort((a, b) => a.localeCompare(b))
        }
        tagEdit = ''
        tagElement.focus()
    }

    function removeTag(tag: string) {
        let tagSet = new Set(form.tags)
        tagSet.delete(tag)
        form.tags = [...tagSet].sort((a, b) => a.localeCompare(b))
        tagEdit = ''
        tagElement.focus()
    }

    function handleFile(e: any) {
        // files = [...files, e.detail.acceptedFiles]
        if (!e.detail.acceptedFiles.length) selectedFile = null
        else {
            selectedFileName = e.detail.acceptedFiles[0].name
            selectedFile = e.detail.acceptedFiles[0]
        }
    }

    let progress: number | null = $state(null)

    let currentXhr: any
    function save() {
        const data = { ...form }
        progress = null
        saving = true
        currentXhr = null

        if (!selectedFile) {
            simpleFetch('POST', `/json/wdom-user/1/user/${$user.id}/interaction`, data).then((r) => {
                saveSuccess(r)
            })
        } else {
            const formData = new FormData()
            formData.append('file', selectedFile)
            formData.append('json', JSON.stringify(data))

            progress = 0
            const xhr = new XMLHttpRequest()
            currentXhr = xhr

            xhr.onreadystatechange = () => {
                if (xhr.readyState == XMLHttpRequest.DONE) {
                    let resp: any = {
                        status: xhr.status,
                        data: null,
                    }
                    if (xhr.responseText) {
                        try {
                            resp.data = JSON.parse(xhr.responseText)
                        } catch (e) {}
                    }

                    saveSuccess(resp)
                }
            }
            xhr.upload.addEventListener(
                'load',
                (e) => {
                    progress = 100
                },
                false,
            )
            xhr.upload.addEventListener(
                'progress',
                (e) => {
                    progress = Math.round((e.loaded * 100) / e.total)
                },
                false,
            )
            xhr.open('POST', `/json/wdom-user/1/user/${$user.id}/interaction`, true)
            xhr.setRequestHeader('X-Lg', $lg)
            // @ts-expect-error
            xhr.setRequestHeader('X-Hydria-Token', session.token)
            xhr.setRequestHeader('Accept', 'application/json')
            xhr.send(formData)
        }
    }

    function abortUpload() {
        currentXhr.abort()
    }

    function saveSuccess(r: any) {
        if (!r.data || !r.data.success) return saveFailure(r)

        saving = false
        collapsed = true
        emptyForm()

        r.data.entry.ctime = toLuxon(r.data.entry.ctime)
        rawDataEntryCache.unshift(r.data.entry)
        importEntries()
    }

    function saveFailure(r: any) {
        console.error('Failed to save entry:', r)
        notify({
            type: 'danger',
            title: 'Journal',
            text: 'L’entrée n’a pas pu être sauvegardée suite à une erreur.',
        })
        saving = false
    }

    let filteredEntries: Interaction[] = $derived(filterEntries(sortedEntries, textFilter, showEmail, showRevisions, showHidden))
    let entryGroups: Group[] = $derived(groupEntries(filteredEntries))

    // $effect(() => {
    //     if (sortedEntries) filterEntries(textFilter, showEmail, showRevisions, showHidden)
    // })
</script>

<div class="card ifl-card">
    <div class="card-title text-primary">
        <div class="ifl-title">Journal et fichiers</div>
        <div class="ifl-text-filter">
            <div class="input-group input-group-sm">
                <input type="text" class="form-control" bind:value={textFilter} />
                <button class="btn btn-outline-secondary" type="button" disabled={textFilter == ''} onclick={() => (textFilter = '')}><Icon inline icon="fa:times" /></button>
            </div>
        </div>
        <div class="ifl-tools">
            <div class="btn-toolbar">
                <div class="btn-group btn-group-sm">
                    <button type="button" class="btn btn-outline-secondary btn-sm" disabled>Tri :</button>
                    <button type="button" class="btn btn-outline-secondary btn-sm" class:active={sortMode == 'ctime'} onclick={() => (sortMode = 'ctime')}><Icon inline icon="fa:clock-o" /></button>
                    <button type="button" class="btn btn-outline-secondary btn-sm" class:active={sortMode == 'domain'} onclick={() => (sortMode = 'domain')}><Icon inline icon="fa:globe" /></button>
                    <button type="button" class="btn btn-outline-secondary btn-sm" class:active={sortMode == 'filename'} onclick={() => (sortMode = 'filename')}><Icon inline icon="fa:file-text-o" /></button>
                    <button type="button" class="btn btn-outline-secondary btn-sm" class:active={sortMode == 'type'} onclick={() => (sortMode = 'type')}><Icon inline icon="fa:folder-o" /></button>
                </div>
                <div class="btn-group">
                    <button class="btn btn-outline-secondary btn-sm" type="button" class:active={showEmail} onclick={() => (showEmail = !showEmail)}>
                        <Icon inline icon={showEmail ? 'fa:check-square-o' : 'fa:square-o'} />
                        Emails seul.
                    </button>
                </div>
                <div class="btn-group">
                    <button type="button" class="btn btn-outline-secondary btn-sm" class:active={showRevisions} onclick={() => (showRevisions = !showRevisions)}>
                        <Icon inline icon={showRevisions ? 'fa:check-square-o' : 'fa:square-o'} />
                        Révisions
                    </button>
                </div>
                <div class="btn-group">
                    <button type="button" class="btn btn-outline-secondary btn-sm" class:active={showHidden} onclick={() => (showHidden = !showHidden)}>
                        <Icon inline icon={showHidden ? 'fa:check-square-o' : 'fa:square-o'} />
                        Masqué
                    </button>
                </div>
            </div>
        </div>
        <div aria-hidden="true" class="ifl-collapse" onclick={() => (collapsed = !collapsed)}>
            <Icon inline icon={collapsed ? 'fa:chevron-right' : 'fa:chevron-down'} />
        </div>
    </div>

    {#if $loading || istatus == 'loading'}
        <div class="card-body">
            <LoadingPattern pattern={['x', 'x', 'x', 'x']} />
        </div>
    {:else if istatus == 'error'}
        <div class="card-body">
            <p class="text-danger">Le journal n’a pas pu être chargé.</p>
        </div>
    {:else}
        {#if saving}
            <div class="card-body">
                <div class="well">
                    <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                    {$_('Please wait…')}
                    {#if progress != null}
                        <div class="help-block">
                            <div style="display: flex; gap: 1em;">
                                <div style="flex: 1 0 0">
                                    <div class="progress">
                                        <div class="progress-bar progress-bar-striped active" style="min-width: 3em; width: {progress}%">
                                            {progress} %
                                        </div>
                                    </div>
                                </div>
                                <div style="flex: 0 0 auto">
                                    <button type="button" class="btn btn-warning" onclick={() => abortUpload()}>Annuler</button>
                                </div>
                            </div>
                        </div>
                    {/if}
                </div>
            </div>
        {:else if !collapsed}
            <div class="card-body">
                <div class="well">
                    <form
                        onsubmit={(e) => {
                            e.preventDefault()
                            save()
                        }}
                        class="d-grid gap-2"
                    >
                        <div class="row" class:is-invalid={!form.content_text.length && (!selectedFile || !selectedFileName)}>
                            <label for="fText" class="col-sm-2 col-form-label">Texte</label>
                            <div class="col-sm-10">
                                <CodeMirror options={{ theme: 'ttcn', lineWrapping: true, lineNumbers: true, readOnly: saving, mode: 'plain' }} bind:value={form.content_text} />
                            </div>
                        </div>
                        <div class="row">
                            <label for="fFile" class="col-sm-2 col-form-label">Fichier</label>
                            <div class="col-sm-10">
                                {#if selectedFile}
                                    <div class="input-group">
                                        <div class="input-group-text">Nom du fichier :</div>
                                        <input type="text" id="fFile" class="form-control" bind:value={selectedFileName} />
                                        <div class="input-group-text">{selectedFile.name}</div>
                                        <div class="input-group-text"><HumanizedBytes value={selectedFile.size} /></div>
                                        <button class="btn btn-outline-secondary" type="button" onclick={() => (selectedFile = null)}><Icon inline icon="fa:trash-o" /></button>
                                    </div>
                                {:else}
                                    <Dropzone on:drop={handleFile} accept="" multiple={false}>
                                        <div style="display: flex; gap: 1em" class:text-danger={!form.content_text.length && (!selectedFile || !selectedFileName)}>
                                            <div style="flex: 0 0 auto"><Icon inline icon="fa:picture-o" /></div>
                                            <div style="flex: 1 0 0">Cliquer pour sélectionner un fichier, ou glisser un fichier directement dans cette boîte.</div>
                                        </div>
                                    </Dropzone>
                                {/if}
                            </div>
                        </div>
                        <div class="row">
                            <label for="fName" class="col-sm-2 col-form-label">Nom</label>
                            <div class="col-sm-10">
                                <input type="text" id="fName" class="form-control" bind:value={form.name} disabled={saving} />
                            </div>
                        </div>
                        <div class="row">
                            <label for="fOrg" class="col-sm-2 col-form-label">Org</label>
                            <div class="col-sm-10">
                                <input type="text" id="fOrg" class="form-control" bind:value={form.org} disabled={saving} />
                            </div>
                        </div>
                        <div class="row">
                            <label for="fEmail" class="col-sm-2 col-form-label">Courriel</label>
                            <div class="col-sm-10">
                                <input type="text" id="fEmail" class="form-control" bind:value={form.email} disabled={saving} />
                            </div>
                        </div>
                        <div class="row">
                            <label for="fPhone" class="col-sm-2 col-form-label">Téléphone</label>
                            <div class="col-sm-10">
                                <input type="text" id="fPhone" class="form-control" bind:value={form.phone} disabled={saving} />
                            </div>
                        </div>
                        <div class="row">
                            <label for="fDomain" class="col-sm-2 col-form-label">Domaine</label>
                            <div class="col-sm-10">
                                <input type="text" id="fDomain" class="form-control" bind:value={form.domain} disabled={saving} />
                            </div>
                        </div>
                        <div class="row">
                            <label for="fTags" class="col-sm-2 col-form-label">Tags</label>
                            <div class="col-sm-10">
                                <div class="input-group">
                                    <input
                                        type="text"
                                        class="form-control"
                                        id="fTags"
                                        disabled={saving}
                                        bind:this={tagElement}
                                        onkeypress={(e) => {
                                            if (e.key === 'Enter' && tagEdit) addTag()
                                        }}
                                        bind:value={tagEdit}
                                    />
                                    <button type="button" class="btn btn-outline-secondary" disabled={saving || !tagEdit} onclick={() => addTag()}>
                                        <Icon inline icon="fa:plus" />
                                    </button>
                                </div>
                            </div>
                            <div class="col-sm-10 offset-sm-2">
                                <div style="display: flex; gap: 0.25em">
                                    {#each form.tags as tag}
                                        <span><span aria-hidden="true" class="label label-primary" style={saving ? '' : 'cursor: pointer'} onclick={() => removeTag(tag)}>{tag} | <Icon inline icon="fa:times" /></span></span>
                                    {/each}
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="offset-sm-2 col-sm-10">
                                {#if saveError}
                                    <div class="help-block">{saveError}</div>
                                {/if}
                                <button type="submit" class="btn btn-outline-secondary" disabled={saving || (!form.content_text.length && (!selectedFile || !selectedFileName))}>Ajouter</button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        {/if}

        <div class="card-body">
            <div class="ifl-groups">
                {#each entryGroups as g}
                    <div class="ifl-group">
                        <div class="iflg-title">{g.title}</div>
                        <div class="iflg-entries">
                            {#each g.entries as e (e.id)}
                                <div class="iflg-entry type-{e.type}" class:latest={e.isLatestRevision} class:older={!e.isLatestRevision} class:is-masked={e.hidden}>
                                    <div class="iflge-main">
                                        {#if e.content_origin == 'custom-email'}
                                            <div class="main-extra clearfix">
                                                {#if e.content_html && e.content_text}
                                                    <div class="main-toggle-type">
                                                        <div class="btn-group btn-group-sm">
                                                            <button type="button" class="btn btn-outline-secondary" onclick={() => (e._contentSelection = 'html')} class:active={e._contentSelection != 'text'} disabled={e._contentSelection != 'text'}>HTML</button>
                                                            <button type="button" class="btn btn-outline-secondary" onclick={() => (e._contentSelection = 'text')} class:active={e._contentSelection == 'text'} disabled={e._contentSelection == 'text'}>Text</button>
                                                        </div>
                                                    </div>
                                                    <strong>Sujet :</strong>
                                                    <span>{asAny(e.content_extra).subject}</span><br />
                                                    {#if !asAny(e.content_extra).sent}
                                                        <strong class="text-danger">Envoi : ERREUR</strong>
                                                    {/if}
                                                {/if}
                                            </div>
                                            {#if e._contentSelection == 'text' && e.content_text}
                                                <div class="main-text">{e.content_text}</div>
                                            {:else if e.content_html}
                                                <div class="main-html">{@html e.content_html}</div>
                                            {/if}
                                        {:else if e.content_text}
                                            <div class="main-text">{e.content_text}</div>
                                        {/if}
                                        {#if e.file}
                                            <div class="main-file">
                                                <span class="mf-filename">
                                                    <TokenAnchorProgress target="_blank" href={`/json/wdom-user/1/${$user.id}/interaction-file/${e.id}/inline`}>{e.file.fullFilename}</TokenAnchorProgress>
                                                </span>
                                                <span class="mf-download">
                                                    <TokenAnchorProgress noDisplay={true} href={`/json/wdom-user/1/${$user.id}/interaction-file/${e.id}/attachment`}><Icon inline icon="fa:download" /></TokenAnchorProgress>
                                                </span>
                                                <span class="mf-filesize tt-below" data-tooltip={`${e.file.size} o`}><HumanizedBytes value={e.file.size} /></span>
                                                <span class="mf-validation-status">
                                                    {#if e.file.content_ok == null}
                                                        <span class="text-warning"><Icon inline icon="fa:question" /></span>
                                                    {:else if !e.file.content_ok}
                                                        <span class="text-danger"><Icon inline icon="fa:ban" /></span>
                                                    {:else}
                                                        <span class="text-success"><Icon inline icon="fa:check" /></span>
                                                    {/if}
                                                </span>
                                                <span class="mf-validation-text">
                                                    {#if fileIdsBeingValidated.has(e.id)}
                                                        <span><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div></span>
                                                    {:else}
                                                        <button type="button" class="btn btn-link btn-a" onclick={() => validateFileContent(e)} disabled={!e.isLatestRevision}>
                                                            {#if e.file.last_verification == null}<span>Valider</span>
                                                            {:else}<span><Date value={e.file.last_verification} format="table-s" /></span>
                                                            {/if}
                                                        </button>
                                                    {/if}
                                                    {#if e.file.last_verification && e.file.content_ok === false}
                                                        <span class="text-danger">Invalide</span>
                                                    {:else if e.file.last_verification && e.file.content_ok === true && e.file.shouldReverify}
                                                        <span class="text-warning">À re-valider</span>
                                                    {/if}
                                                </span>
                                            </div>
                                        {/if}
                                    </div>
                                    <div class="iflge-stamp">
                                        <div class="iflgee-date tt-left" style="cursor: help; text-decoration: dashed underline #999;" data-tooltip={e.ctime.toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS, { locale: $luxonLocale })}><Date value={e.ctime} format="DATE_FULL" /></div>
                                        <div class="iflgee-mask">
                                            {#if entryIdsBeingMasked.has(e.id)}
                                                <span><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div></span>
                                            {:else}
                                                <button type="button" class="btn btn-link btn-a" onclick={() => toggleMask(e)}>
                                                    <span>{e.hidden ? 'Activer' : 'Masquer'}</span>
                                                </button>
                                            {/if}
                                        </div>
                                    </div>
                                    <div class="iflge-source">
                                        <div class="iflgee-source-id">
                                            <a use:sanchor href={spath(`/user/${e.author_id}`)}>{e.author_name}</a>
                                        </div>
                                        <div class="iflgee-source-ip">{e.author_ip}</div>
                                    </div>
                                    <div class="iflge-extra">
                                        {#if e.name}
                                            <div class="iflge-name">
                                                <span class="extra-label">Nom</span>
                                                <span class="extra-value">{e.name}</span>
                                            </div>
                                        {/if}
                                        {#if e.org}
                                            <div class="iflge-org">
                                                <span class="extra-label">Org</span>
                                                <span class="extra-value">{e.org}</span>
                                            </div>
                                        {/if}
                                        {#if e.email}
                                            <div class="iflge-email">
                                                <span class="extra-label">Courriel</span>
                                                <span class="extra-value">{e.email}</span>
                                            </div>
                                        {/if}
                                        {#if e.phone}
                                            <div class="iflge-phone">
                                                <span class="extra-label">Tél.</span>
                                                <span class="extra-value">
                                                    {#if e.phone.match(/^\+\d+\.\d+/)}
                                                        <InternationalPhone phone={e.phone} />
                                                    {:else}
                                                        {e.phone}
                                                    {/if}
                                                </span>
                                            </div>
                                        {/if}
                                        {#if e.domain}
                                            <div class="iflge-domain">
                                                <span class="extra-label">Domaine</span>
                                                <span class="extra-value">{e.domain}</span>
                                            </div>
                                        {/if}
                                        {#if e.tags && e.tags.length}
                                            <div class="iflge-tags">
                                                <span class="extra-label">Tags</span>
                                                <span class="extra-value"
                                                    >{#each e.tags as tag}<span>{tag}</span>{/each}
                                                </span>
                                            </div>
                                        {/if}
                                    </div>
                                </div>
                            {/each}
                        </div>
                    </div>
                {/each}
            </div>
        </div>
    {/if}
</div>
